/**
 * AccountDetailSheet
 *
 * A side-panel drawer that renders rich details for a single account.
 * Provides inline Edit and Delete action buttons.
 * Follows the same design pattern as TransactionDetailSheet.
 *
 * [T64P0] Account Detail Sheet — consistent detail-on-click pattern across all pages
 */
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { formatCurrency, getAccountTypeLabel } from '@/lib/utils';
import {
  Pencil,
  Trash2,
  Wallet,
  Landmark,
  CreditCard,
  TrendingUp,
  Clock,
  Banknote,
  Hash,
  FileText,
  Building2,
  Activity,
  Percent,
  ShieldCheck,
  Eye,
  EyeOff,
} from 'lucide-react';
import { format } from 'date-fns';
import type { Account } from '@/lib/types';

// ---------------------------------------------------------------------------
// Per-type visual config
// ---------------------------------------------------------------------------

const accountTypeConfig: Record<string, {
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
}> = {
  checking: { icon: Landmark, iconColor: 'text-blue-600 dark:text-blue-400', iconBg: 'bg-blue-100 dark:bg-blue-900/30' },
  savings: { icon: TrendingUp, iconColor: 'text-green-600 dark:text-green-400', iconBg: 'bg-green-100 dark:bg-green-900/30' },
  credit_card: { icon: CreditCard, iconColor: 'text-purple-600 dark:text-purple-400', iconBg: 'bg-purple-100 dark:bg-purple-900/30' },
  investment: { icon: TrendingUp, iconColor: 'text-emerald-600 dark:text-emerald-400', iconBg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  loan: { icon: CreditCard, iconColor: 'text-orange-600 dark:text-orange-400', iconBg: 'bg-orange-100 dark:bg-orange-900/30' },
  mortgage: { icon: Landmark, iconColor: 'text-rose-600 dark:text-rose-400', iconBg: 'bg-rose-100 dark:bg-rose-900/30' },
  manual: { icon: Wallet, iconColor: 'text-gray-600 dark:text-gray-400', iconBg: 'bg-gray-100 dark:bg-gray-900/30' },
  crypto: { icon: TrendingUp, iconColor: 'text-amber-600 dark:text-amber-400', iconBg: 'bg-amber-100 dark:bg-amber-900/30' },
  other: { icon: Wallet, iconColor: 'text-gray-600 dark:text-gray-400', iconBg: 'bg-gray-100 dark:bg-gray-900/30' },
};

// ---------------------------------------------------------------------------
// Helper: one detail row
// ---------------------------------------------------------------------------

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
          {label}
        </p>
        <div className="text-sm text-foreground">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AccountDetailSheetProps {
  account: Account | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (account: Account) => void;
  onDelete?: (account: Account) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AccountDetailSheet({
  account,
  open,
  onOpenChange,
  onEdit,
  onDelete,
}: AccountDetailSheetProps) {
  if (!account) return null;

  const config = accountTypeConfig[account.accountType] ?? accountTypeConfig.other;
  const Icon = config.icon;

  const handleEdit = () => {
    onOpenChange(false);
    onEdit?.(account);
  };

  const handleDelete = () => {
    onOpenChange(false);
    onDelete?.(account);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[480px] h-[100dvh] flex flex-col p-0 gap-0 overflow-hidden pb-[env(safe-area-inset-bottom)]"
      >
        {/* Hero area */}
        <div className={`px-6 pt-8 pb-6 ${config.iconBg}`}>
          <SheetHeader className="space-y-4">
            <div className="flex items-center gap-4">
              <div
                className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/80 dark:bg-black/20 shadow-sm"
                style={{ backgroundColor: account.color ? `${account.color}20` : undefined }}
              >
                {account.emoji ? (
                  <span className="text-2xl leading-none">{account.emoji}</span>
                ) : (
                  <Icon className={`h-7 w-7 ${config.iconColor}`} />
                )}
              </div>
              <div className="min-w-0">
                <SheetTitle className="text-left leading-tight line-clamp-2">
                  {account.name}
                </SheetTitle>
                <SheetDescription className="sr-only">Account details</SheetDescription>
                <Badge className="mt-1.5 capitalize">
                  {getAccountTypeLabel(account.accountType)}
                </Badge>
              </div>
            </div>

            {/* Balance */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Balance
              </p>
              <p className={`text-3xl font-bold tabular-nums ${account.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatCurrency(account.balance, account.currency || 'USD')}
              </p>
            </div>

            {/* Status badges */}
            <div className="flex flex-wrap gap-2">
              <Badge variant={account.isActive ? 'outline' : 'secondary'}>
                {account.isActive ? (
                  <><Eye className="h-3 w-3 mr-1" /> Active</>
                ) : (
                  <><EyeOff className="h-3 w-3 mr-1" /> Inactive</>
                )}
              </Badge>
              {account.isManual && (
                <Badge variant="secondary">Manual</Badge>
              )}
              {account.syncStatus && account.syncStatus !== 'never' && (
                <Badge variant="outline" className="gap-1">
                  <Activity className="h-3 w-3" />
                  Sync: {account.syncStatus}
                </Badge>
              )}
            </div>
          </SheetHeader>
        </div>

        {/* Details body */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-4">
            <DetailRow icon={Banknote} label="Currency">
              {account.currency || 'USD'}
            </DetailRow>

            {(account.institutionName || account.institution) && (
              <DetailRow icon={Building2} label="Institution">
                {account.institutionName || account.institution}
              </DetailRow>
            )}

            {account.accountNumber && (
              <DetailRow icon={Hash} label="Account Number">
                ••••{account.accountNumber}
              </DetailRow>
            )}

            {account.creditLimit != null && account.creditLimit > 0 && (
              <DetailRow icon={CreditCard} label="Credit Limit">
                {formatCurrency(account.creditLimit, account.currency || 'USD')}
              </DetailRow>
            )}

            {account.availableBalance != null && (
              <DetailRow icon={Wallet} label="Available Balance">
                {formatCurrency(account.availableBalance, account.currency || 'USD')}
              </DetailRow>
            )}

            {account.interestRate != null && account.interestRate > 0 && (
              <DetailRow icon={Percent} label="Interest Rate">
                {account.interestRate}%
              </DetailRow>
            )}

            {account.notes && (
              <DetailRow icon={FileText} label="Notes">
                <span className="text-muted-foreground whitespace-pre-wrap">
                  {account.notes}
                </span>
              </DetailRow>
            )}

            {account.lastSyncedAt && (
              <DetailRow icon={Activity} label="Last Synced">
                <span className="text-muted-foreground">
                  {format(new Date(account.lastSyncedAt), 'MMM d, yyyy • h:mm a')}
                </span>
              </DetailRow>
            )}

            <Separator className="my-2" />

            {/* Timestamps */}
            <div className="space-y-3">
              <DetailRow icon={Clock} label="Created">
                <span className="text-muted-foreground">
                  {format(new Date(account.createdAt), 'MMM d, yyyy • h:mm a')}
                </span>
              </DetailRow>
              {account.updatedAt !== account.createdAt && (
                <DetailRow icon={Clock} label="Last Updated">
                  <span className="text-muted-foreground">
                    {format(new Date(account.updatedAt), 'MMM d, yyyy • h:mm a')}
                  </span>
                </DetailRow>
              )}
            </div>

            {/* Security info */}
            {account.plaidAccountId && (
              <>
                <Separator className="my-2" />
                <DetailRow icon={ShieldCheck} label="Plaid Connected">
                  <span className="text-muted-foreground font-mono text-xs">
                    {account.plaidAccountId}
                  </span>
                </DetailRow>
              </>
            )}
          </div>
        </div>

        {/* Footer actions */}
        {(onEdit || onDelete) && (
          <SheetFooter className="sticky bottom-0 border-t bg-background px-4 sm:px-6 py-3 gap-2 flex-row justify-end">
            {onDelete && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={handleEdit}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit Account
              </Button>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
